"use client";

/**
 * ------------------------------------------------------------
 * Developer Name : Praveen Tripathi
 * Purpose        : MBA landing page header bar (sticky owned by LandingStickyChrome)
 * ------------------------------------------------------------
 */

import styles from "@/components/molecules/LandingPageHeader/styles.module.scss";
import cn from "clsx";
import { useEffect } from "react";
import { useModalDispatch } from "@/context/ModalContext";
import { pushToDataLayer } from "@/helpers/gtag";
import { getGlobalStateValue } from "@/context/utils";
import { usePathname } from "next/navigation";
import RightArrowNew from "@/SvgIcons/RightArrowNew";
import stylesNew from './MbaLandingHeader.module.scss';
import { IModalActions, ModalTypes } from "@/types/modal";
import { resolveMbaPhoneHeader } from "@/features/landing-program/utils/resolveMbaPhoneHeader";
import { getLandingRegion } from "@/features/landing-program/utils/landingRegion";
import { useHeaderLogo } from "@/features/site-chrome/view/useHeaderLogo";
import { IMAGE_ASSETS } from "@/constants/images";
import UnivoImage from '@/components/common/UnivoImage';

export default function MbaLandingPageHeader({
  data
}) {
  const headerFooterData = data?.headerFooterData || {};
  const { header, isCROPage, headerMenu ,amsidDetails} = headerFooterData;
  const modalDispatch = useModalDispatch();
  const device = data?.device;
  const isMobile = device === "mobile";
  const pathName = usePathname();
  const slug = data?.slugs?.join("/") || "";
  // Resolve the CMS logo against the server-provided slug so the match does not
  // depend on the client router's pathname (which the catch-all route can
  // report with device/country segments).
  const cmsLogo = useHeaderLogo(slug ? `/${slug}` : undefined);
  const legacyLogoUrl = header?.Logo?.source?.data?.[0]?.attributes?.url;
  const { updatedheader, isPhoneNumber } = resolveMbaPhoneHeader(header, slug);
  // Server slug, same reason as the logo above.
  const region = getLandingRegion(slug);
  const logoUrl = region?.logoUrl || cmsLogo?.src || legacyLogoUrl || IMAGE_ASSETS.amityLogoHeader;
  const logoAlt = cmsLogo?.alt || header?.Logo?.alt || "Amity University Online";
  const hasLogo = Boolean(logoUrl);

  useEffect(() => {
    pushToDataLayer({
      event: "page_type",
      page_type: getGlobalStateValue("pageType"),
    });
  }, []);

  if (!header?.enabled) return null;

  const scrollToLeadForm = (scrollTo) => {
    document.getElementById(scrollTo)?.scrollIntoView({
      block: "start",
    });
  };

  const GAEventsHandler = () => {
    pushToDataLayer({
      event: "logo_click",
      section_name: "top navigation bar",
      page_type: getGlobalStateValue("pageType"),
    });
  };

  const onClickCTAHandler = () => {
    if (pathName.includes("/lp-b2i")) {
      modalDispatch({
        type: IModalActions.OpenModal,
        data: {
          modalType: ModalTypes.ConnectModalPartner,
          modalData: { 
            heading: "Get Expert Guidance",
            submitBtnText: "Book Free Session",
            rfiCase: "PARTNER"
          },
        },
      });
      return;
    }
    if (pathName.includes("/professor")) {
      modalDispatch({
        type: IModalActions.OpenModal,
        data: {
          modalType: ModalTypes.ProfessorModal,
        },
      });
      return;
    }
    if (updatedheader?.CTA?.gaEvents) {
      updatedheader.CTA.gaEvents();
    } else {
      pushToDataLayer({
        event: "enquire_now_enroll_now_cta_click",
        cta_text: data?.CTA?.text || "NA",
        section_name: "Top Navigation Bar",
        page_type: "Campaign Page",
      });
    }
    scrollToLeadForm("#leadform");
  };

  const CustomButtonPhone = () => {
    return (
      <>
        {isMobile ? (
          <a
            href={`${isMobile ? updatedheader?.CTA?.href : "#"}`}
            className={styles.btn}
            style={{ fontFamily: "none" }}
            target={"_self"}
            onClick={onClickCTAHandler}
          >
            Call Now
          </a>
        ) : (
          <button
            id="enrollnow-lp"
            onClick={onClickCTAHandler}
            className={styles.btn}
            style={isPhoneNumber ? { fontFamily: "none" } : {}}
          >
            {updatedheader?.CTA?.text}
          </button>
        )}
      </>
    );
  };

  const croRFIHandler = () => {
    modalDispatch({
      type: IModalActions.OpenModal,
      data: {
        modalType: ModalTypes.ConnectModalMba,
        modalData: {
          isLambdaAPI: true,
          rfiCase: "THANK_YOU_PAGE_CASE",
          associatedPrograms: headerMenu?.associatedPrograms,
        },
      },
    });
  };

  const CustomButton = () => {

    if (pathName.includes("/lp-b2i")) {
      return <button onClick={onClickCTAHandler} className={stylesNew.enrollNowCROPage} id="enrollnow-lp">
        Apply Now
      </button>
    }

    if (pathName.includes("/professor")) {
      return <button onClick={onClickCTAHandler} className={stylesNew.enrollNowCROPage} id="enrollnow-lp">
        Join Now
      </button>
    }

    if (isCROPage) {
      return <button onClick={croRFIHandler} className={stylesNew.enrollNowCROPage} id="enrollnow-lp">
          Apply Now
      </button>
    }


    return header?.CTA?.url?.includes("#") ? (
      <>
        <button
          id="enrollnow-lp"
          onClick={onClickCTAHandler}
          className={styles.btn}
        >
          {header?.CTA?.text} <RightArrowNew stroke="#1a325d" />
        </button>
      </>
    ) : (
      <a
        href={header?.CTA?.url || ""}
        className={styles.btn}
        target={header?.CTA?.target?.[0] || "_self"}
      >
        {header?.CTA?.text} &#8594;
      </a>
    );
  }

  const logoImage = hasLogo ? (
    <UnivoImage
      id="logo-lp"
      width={400}
      height={100}
      onClick={GAEventsHandler}
      src={logoUrl}
      alt={logoAlt}
      className={styles.logo}
    />
  ) : null;

  return (
    <header
      className={cn("container", styles.header)}
      data-site-header
    >
      <div className={styles.headerContainer}>
          {pathName.includes("/lp-b2i") ? (
            <div className={stylesNew.b2isection}>
            {hasLogo ? (
                <>
                  <UnivoImage
                    id="logo-lp"
                    width={400}
                    height={100}
                    onClick={GAEventsHandler}
                    src={logoUrl}
                    alt={logoAlt}
                    className={cn(styles.logo, stylesNew.b2ilogo)}
                  />
                </>
              ) : null}
              {amsidDetails?.logo ? (
                <>
                <span className={stylesNew.divider}/>
                <UnivoImage
                  id="logo-lp"
                  width={400}
                  height={100}
                  onClick={GAEventsHandler}
                  src={amsidDetails.logo}
                  alt='logo'
                  className={cn(styles.logo, stylesNew.b2ilogo)}
                />
                </>
              ) : amsidDetails?.Name ? (
                <>
                <span className={stylesNew.divider}/>
                <div className={stylesNew.schoolname} dangerouslySetInnerHTML={{ __html: amsidDetails.Name }} />
                </>
              ) : null}
            </div>
          ) : region ? (
            <div className={stylesNew.regionSection}>
              {logoImage}
            </div>
          ) : logoImage}

        {header?.CTA?.enabled &&
          pathName !== "/isat" &&
          pathName !== "/msat" &&
          pathName !== "/msat-thankyou" ? (
          isPhoneNumber ? <CustomButtonPhone /> :
            <CustomButton />
        ) : null}
      </div>
    </header>
  );
}
